// src/components/CartModal.js
import React, { useState, useEffect } from 'react';
import { _getFromLocalStorage, _saveToLocalStorage } from '../api/apiService';

const bs = { fontFamily: "'Montserrat', sans-serif" };
const ds = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const WA_NUMBER = '525569380380';

const buildWhatsAppMessage = (cartItems, total) => {
  const lines = [
    '¡Hola! Me gustaría realizar el siguiente pedido:',
    '',
    ...cartItems.map(item =>
      `• ${item.productName} (${item.variantInfo}) × ${item.quantity} — $${(item.price * item.quantity).toFixed(2)}`
    ),
    '',
    `*Total: $${total.toFixed(2)} MXN*`,
    '',
    '¿Me pueden confirmar disponibilidad y forma de pago? ¡Gracias!',
  ];
  return lines.join('\n');
};

const CartModal = ({ onClose }) => {
  const [cartItems, setCartItems] = useState([]);
  const [visible,   setVisible]   = useState(false);

  useEffect(() => {
    setCartItems(_getFromLocalStorage('publicCart') || []);
    requestAnimationFrame(() => setVisible(true));
  }, []);

  useEffect(() => {
    const esc = e => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const updateQty = (index, delta) => {
    const updated = [...cartItems];
    const newQty  = updated[index].quantity + delta;
    if (newQty <= 0) {
      const filtered = cartItems.filter((_, i) => i !== index);
      setCartItems(filtered);
      _saveToLocalStorage('publicCart', filtered);
      window.dispatchEvent(new Event('storage'));
      return;
    }
    updated[index].quantity = newQty;
    setCartItems(updated);
    _saveToLocalStorage('publicCart', updated);
    window.dispatchEvent(new Event('storage'));
  };

  const removeItem = (index) => {
    const updated = cartItems.filter((_, i) => i !== index);
    setCartItems(updated);
    _saveToLocalStorage('publicCart', updated);
    window.dispatchEvent(new Event('storage'));
  };

  const handleWhatsApp = () => {
    if (!cartItems.length) return;
    const msg = buildWhatsAppMessage(cartItems, total);
    const url = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  const total = cartItems.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <>
      {/* Overlay */}
      <div onClick={handleClose} style={{
        position: 'fixed', inset: 0, zIndex: 150,
        background: 'rgba(6,13,20,0.5)', backdropFilter: 'blur(3px)',
        opacity: visible ? 1 : 0, transition: 'opacity 0.3s ease',
      }} />

      {/* Panel lateral */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 151,
        width: '100%', maxWidth: '420px', background: 'white',
        display: 'flex', flexDirection: 'column',
        transform: visible ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.35s cubic-bezier(0.4,0,0.2,1)',
        boxShadow: '-20px 0 60px rgba(0,0,0,0.15)',
      }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', padding: '20px 24px',
          borderBottom: '1px solid #f0eaea' }}>
          <div>
            <h2 style={{ ...ds, fontSize: '1.6rem', fontWeight: 600,
              color: '#060D14', margin: 0, lineHeight: 1 }}>Tu Carrito</h2>
            {cartItems.length > 0 && (
              <p style={{ ...bs, fontSize: '11px', color: '#bbb', margin: '4px 0 0' }}>
                {cartItems.reduce((n, i) => n + i.quantity, 0)} producto{cartItems.reduce((n, i) => n + i.quantity, 0) !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <button onClick={handleClose} style={{ background: 'none', border: 'none',
            cursor: 'pointer', color: '#aaa', padding: '4px', display: 'flex',
            transition: 'color 0.15s' }}
          onMouseOver={e => e.currentTarget.style.color = '#961914'}
          onMouseOut={e => e.currentTarget.style.color = '#aaa'}>
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Items */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {cartItems.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', padding: '48px 24px', gap: '16px' }}>
              <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="#e5c9c9">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
              </svg>
              <p style={{ ...bs, color: '#ccc', fontSize: '13px', textAlign: 'center' }}>
                Tu carrito está vacío
              </p>
              <button onClick={handleClose} style={{ ...bs, background: 'none',
                border: '1px solid #e5c9c9', color: '#961914', padding: '10px 24px',
                cursor: 'pointer', fontSize: '10px', fontWeight: 600,
                letterSpacing: '0.18em', textTransform: 'uppercase',
                borderRadius: '1px', transition: 'all 0.2s' }}
              onMouseOver={e => { e.currentTarget.style.background = '#961914'; e.currentTarget.style.color = 'white'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#961914'; }}>
                Explorar Tienda
              </button>
            </div>
          ) : (
            cartItems.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: '14px',
                padding: '16px 24px', borderBottom: '1px solid #faf5f5' }}>

                {/* Imagen */}
                <div style={{ width: '72px', height: '72px', flexShrink: 0,
                  borderRadius: '2px', overflow: 'hidden', background: '#f5f2f2' }}>
                  {item.image
                    ? <img src={item.image} alt={item.productName}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <div style={{ width: '100%', height: '100%', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', color: '#ddd' }}>
                        <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586"/>
                        </svg>
                      </div>
                  }
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ ...ds, fontSize: '1rem', fontWeight: 600,
                    color: '#060D14', margin: '0 0 3px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.productName}
                  </p>
                  <p style={{ ...bs, fontSize: '11px', color: '#bbb', margin: '0 0 10px' }}>
                    {item.variantInfo}
                  </p>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* +/− */}
                    <div style={{ display: 'flex', alignItems: 'center',
                      border: '1px solid #e5e0e0', borderRadius: '1px' }}>
                      <button onClick={() => updateQty(i, -1)} style={{
                        width: '28px', height: '28px', background: 'none', border: 'none',
                        cursor: 'pointer', fontSize: '16px', color: '#888',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'color 0.15s' }}
                      onMouseOver={e => e.currentTarget.style.color = '#961914'}
                      onMouseOut={e => e.currentTarget.style.color = '#888'}>−</button>
                      <span style={{ ...bs, width: '28px', textAlign: 'center',
                        fontSize: '12px', fontWeight: 600, color: '#060D14',
                        borderLeft: '1px solid #f0eaea', borderRight: '1px solid #f0eaea' }}>
                        {item.quantity}
                      </span>
                      <button onClick={() => updateQty(i, 1)} style={{
                        width: '28px', height: '28px', background: 'none', border: 'none',
                        cursor: 'pointer', fontSize: '16px', color: '#888',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'color 0.15s' }}
                      onMouseOver={e => e.currentTarget.style.color = '#961914'}
                      onMouseOut={e => e.currentTarget.style.color = '#888'}>+</button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ ...ds, fontSize: '1.15rem', color: '#961914' }}>
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                      <button onClick={() => removeItem(i)} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#ddd', padding: '2px', transition: 'color 0.15s' }}
                      onMouseOver={e => e.currentTarget.style.color = '#dc2626'}
                      onMouseOut={e => e.currentTarget.style.color = '#ddd'}>
                        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div style={{ borderTop: '1px solid #f0eaea', padding: '20px 24px' }}>

            {/* Total */}
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ ...bs, fontSize: '11px', fontWeight: 600,
                letterSpacing: '0.18em', textTransform: 'uppercase', color: '#aaa' }}>
                Total
              </span>
              <span style={{ ...ds, fontSize: '1.9rem', color: '#060D14', fontWeight: 400 }}>
                ${total.toFixed(2)}
              </span>
            </div>

            {/* Botón WhatsApp */}
            <button onClick={handleWhatsApp} style={{
              ...bs, width: '100%', padding: '15px',
              background: '#25D366', color: 'white', border: 'none',
              cursor: 'pointer', fontSize: '11px', fontWeight: 600,
              letterSpacing: '0.15em', textTransform: 'uppercase',
              borderRadius: '1px', transition: 'background 0.2s', marginBottom: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            }}
            onMouseOver={e => e.currentTarget.style.background = '#1ebe5d'}
            onMouseOut={e => e.currentTarget.style.background = '#25D366'}>
              {/* Ícono WhatsApp */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Enviar Pedido por WhatsApp
            </button>

            <button onClick={handleClose} style={{
              ...bs, width: '100%', padding: '11px',
              background: 'none', color: '#999', border: '1px solid #e5e0e0',
              cursor: 'pointer', fontSize: '10px', fontWeight: 600,
              letterSpacing: '0.15em', textTransform: 'uppercase',
              borderRadius: '1px', transition: 'all 0.2s',
            }}
            onMouseOver={e => { e.currentTarget.style.borderColor = '#961914'; e.currentTarget.style.color = '#961914'; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = '#e5e0e0'; e.currentTarget.style.color = '#999'; }}>
              Seguir Comprando
            </button>

            {/* Nota WhatsApp */}
            <p style={{ ...bs, fontSize: '10px', color: '#ccc', textAlign: 'center',
              marginTop: '12px', lineHeight: 1.5 }}>
              Al enviar por WhatsApp nos contactarás directamente para confirmar tu pedido y coordinar el pago.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default CartModal;
